import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, Filter, X, ChevronDown } from "lucide-react";
import { TIME_RANGE_PRESETS } from "./chartConstants";

/**
 * Barre de filtres globale pour la page Graphiques.
 * Props :
 *  - timeRange, setTimeRange, startDate, setStartDate, endDate, setEndDate
 *  - orderTypes [{id,name}], selectedOrderTypeIds, setSelectedOrderTypeIds
 *  - sellersList [{id,name,archived,virtual}], selectedSellerIds, setSelectedSellerIds
 *  - sellerDisplayMode: "cumul" | "distinct" — set via parent
 *  - appConfig { fiscal_year_start_month, fiscal_year_start_day }, onFiscalConfigChange (m,d)
 *  - canEditFiscal: bool
 */
export default function ChartFiltersBar({
  timeRange, setTimeRange, startDate, setStartDate, endDate, setEndDate,
  orderTypes, selectedOrderTypeIds, setSelectedOrderTypeIds,
  sellersList, selectedSellerIds, setSelectedSellerIds,
  sellerDisplayMode, setSellerDisplayMode,
  appConfig, onFiscalConfigChange, canEditFiscal,
}) {
  const [typesOpen, setTypesOpen] = useState(false);
  const [sellersOpen, setSellersOpen] = useState(false);
  const [fiscalOpen, setFiscalOpen] = useState(false);
  const [fiscalMonth, setFiscalMonth] = useState(appConfig?.fiscal_year_start_month || 1);
  const [fiscalDay, setFiscalDay] = useState(appConfig?.fiscal_year_start_day || 1);

  const selectedTypeNames = orderTypes
    .filter((t) => selectedOrderTypeIds.includes(t.id))
    .map((t) => t.name);
  const selectedSellerNames = sellersList
    .filter((s) => selectedSellerIds.includes(s.id))
    .map((s) => s.name);

  const toggle = (arr, setArr, id) => {
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Filter className="h-4 w-4" /> Filtres
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Période */}
          <div>
            <Label className="mb-1.5 block text-xs">Période</Label>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger data-testid="filter-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_RANGE_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {timeRange === "current_fiscal_year" && canEditFiscal && (
              <Popover open={fiscalOpen} onOpenChange={setFiscalOpen}>
                <PopoverTrigger asChild>
                  <Button variant="link" size="sm" className="text-[11px] h-auto p-0 mt-1">
                    Configurer (début : {String(appConfig?.fiscal_year_start_day || 1).padStart(2, "0")}/{String(appConfig?.fiscal_year_start_month || 1).padStart(2, "0")})
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 space-y-2">
                  <p className="text-xs text-muted-foreground">Date de début de l'année comptable.</p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Jour</Label>
                      <Input type="number" min={1} max={31} value={fiscalDay}
                        onChange={(e) => setFiscalDay(parseInt(e.target.value, 10) || 1)} />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Mois</Label>
                      <Input type="number" min={1} max={12} value={fiscalMonth}
                        onChange={(e) => setFiscalMonth(parseInt(e.target.value, 10) || 1)} />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    data-testid="save-fiscal-config"
                    onClick={() => { onFiscalConfigChange(fiscalMonth, fiscalDay); setFiscalOpen(false); }}
                  >
                    Enregistrer
                  </Button>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Custom dates */}
          {timeRange === "custom" && (
            <>
              <div>
                <Label className="mb-1.5 block text-xs">Du</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left text-xs">
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {startDate ? format(startDate, "dd/MM/yyyy") : "Début"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} locale={fr} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">Au</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left text-xs">
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {endDate ? format(endDate, "dd/MM/yyyy") : "Fin"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} locale={fr} />
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}

          {/* Types de commande */}
          <div>
            <Label className="mb-1.5 block text-xs">Type(s) de commande</Label>
            <Popover open={typesOpen} onOpenChange={setTypesOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between text-xs font-normal" data-testid="filter-order-types">
                  <span className="truncate">
                    {selectedTypeNames.length === 0 ? "Tous les types"
                      : selectedTypeNames.length === 1 ? selectedTypeNames[0]
                      : `${selectedTypeNames.length} types`}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2 max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium">Sélectionne les types</p>
                  {selectedOrderTypeIds.length > 0 && (
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setSelectedOrderTypeIds([])}>
                      <X className="h-3 w-3 mr-1" /> Effacer
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {orderTypes.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1">
                      <Checkbox
                        checked={selectedOrderTypeIds.includes(t.id)}
                        onCheckedChange={() => toggle(selectedOrderTypeIds, setSelectedOrderTypeIds, t.id)}
                      />
                      <span className="text-sm">{t.name}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Vendeurs */}
          <div>
            <Label className="mb-1.5 block text-xs">Vendeur(s)</Label>
            <Popover open={sellersOpen} onOpenChange={setSellersOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between text-xs font-normal" data-testid="filter-sellers">
                  <span className="truncate">
                    {selectedSellerNames.length === 0 ? "Tous les vendeurs"
                      : selectedSellerNames.length === 1 ? selectedSellerNames[0]
                      : `${selectedSellerNames.length} vendeurs`}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2 max-h-80 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium">Sélection</p>
                  {selectedSellerIds.length > 0 && (
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setSelectedSellerIds([])}>
                      <X className="h-3 w-3 mr-1" /> Effacer
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {sellersList.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded px-2 py-1">
                      <Checkbox
                        checked={selectedSellerIds.includes(s.id)}
                        onCheckedChange={() => toggle(selectedSellerIds, setSelectedSellerIds, s.id)}
                      />
                      <span className="text-sm flex-1">{s.name}</span>
                      {s.archived && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">archivé</span>}
                      {s.virtual && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">virtuel</span>}
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {selectedSellerIds.length > 1 && (
              <div className="mt-1.5 flex items-center gap-2">
                <Label className="text-[11px] text-muted-foreground">Affichage :</Label>
                <Select value={sellerDisplayMode} onValueChange={setSellerDisplayMode}>
                  <SelectTrigger className="h-7 text-xs w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cumul">Cumulé</SelectItem>
                    <SelectItem value="distinct">Distinct (par vendeur)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
